// src/modules/app-views/fiscalConfig/pages/Document/tabs/NfceTab/modals/CancelNfceModal/Step1.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Typography, Alert, Input, Form, Button, Space, Divider } from 'antd';
import {
  CloseCircleOutlined,
  CheckCircleOutlined,
  DownloadOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import SimpleTextArea from 'components/SimpleComponents/SimpleTextArea';
import translateX from 'utils/translateX';

const { Text, Paragraph } = Typography;

// *******************************************************************************
// HELPER - Download XML file
// *******************************************************************************
const downloadXml = (xmlContent, fileName) => {
  if (!xmlContent) return;

  const blob = new Blob([xmlContent], { type: 'application/xml' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName || 'cancelamento.xml';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// *******************************************************************************
// HELPER - Download from URL (fetch as blob to force download)
// *******************************************************************************
const downloadFromUrl = async (url, fileName) => {
  if (!url) return;

  try {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName || 'cancelamento.xml';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  } catch (err) {
    console.error('[CancelNfceModal] Failed to download from URL:', err);
    window.open(url, '_blank');
  }
};

// *******************************************************************************
// ERROR VIEW COMPONENT
// *******************************************************************************
const CancelErrorView = ({ resultData }) => {
  const { error, code, xml, xmlStorageUrl } = resultData || {};

  const handleDownloadXml = () => {
    const fileName = 'cancelamento_rejeitado.xml';

    if (xmlStorageUrl) {
      downloadFromUrl(xmlStorageUrl, fileName);
    } else if (xml) {
      downloadXml(xml, fileName);
    }
  };

  const hasDownloadOption = xmlStorageUrl || xml;

  return (
    <div style={{ paddingTop: 8 }}>
      <Alert
        type="error"
        showIcon
        icon={<CloseCircleOutlined />}
        message={translateX('cancel_nfce_error_title')}
        description={error || translateX('cancel_nfce_error')}
        style={{ marginBottom: 16 }}
      />

      {code && (
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">{translateX('error_code')}: </Text>
          <Text strong style={{ fontSize: 14 }}>
            {code}
          </Text>
        </div>
      )}

      <Divider />

      {hasDownloadOption && (
        <div style={{ marginTop: 16 }}>
          <Paragraph type="secondary" style={{ marginBottom: 12 }}>
            {translateX('cancel_nfce_download_rejection_info')}
          </Paragraph>
          <Space>
            <Button icon={<DownloadOutlined />} onClick={handleDownloadXml}>
              {translateX('download')}
            </Button>
          </Space>
        </div>
      )}
    </div>
  );
};

// *******************************************************************************
// RESULT VIEW COMPONENT
// *******************************************************************************
const CancelResultView = ({ resultData, invoiceNumber }) => {
  const { protocolNumber, cancelDate, invoiceId, xml, xmlStorageUrl } = resultData || {};

  const displayNumber = invoiceNumber || invoiceId || '-';

  const handleDownloadXml = () => {
    const fileName = protocolNumber ? `cancelamento_${protocolNumber}.xml` : 'cancelamento.xml';

    if (xmlStorageUrl) {
      downloadFromUrl(xmlStorageUrl, fileName);
    } else if (xml) {
      downloadXml(xml, fileName);
    }
  };

  const hasDownloadOption = xmlStorageUrl || xml;

  return (
    <div style={{ paddingTop: 8 }}>
      <Alert
        type="success"
        showIcon
        icon={<CheckCircleOutlined />}
        message={translateX('cancel_nfce_success_title')}
        description={translateX('cancel_nfce_success_description')}
        style={{ marginBottom: 16 }}
      />

      <div style={{ marginBottom: 16 }}>
        <Text type="secondary">{translateX('invoice_number')}: </Text>
        <Text strong style={{ fontSize: 14 }}>
          {displayNumber}
        </Text>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Text type="secondary">{translateX('protocol_number')}: </Text>
        <Text strong style={{ fontSize: 16 }}>
          {protocolNumber || '-'}
        </Text>
      </div>

      {cancelDate && (
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">{translateX('cancel_date')}: </Text>
          <Text strong>{new Date(cancelDate).toLocaleString('pt-BR')}</Text>
        </div>
      )}

      <Divider />

      {hasDownloadOption && (
        <div style={{ marginTop: 16 }}>
          <Paragraph type="secondary" style={{ marginBottom: 12 }}>
            {translateX('cancel_nfce_download_info')}
          </Paragraph>
          <Space>
            <Button type="primary" icon={<DownloadOutlined />} onClick={handleDownloadXml}>
              {translateX('download')}
            </Button>
          </Space>
        </div>
      )}

      {!hasDownloadOption && (
        <div style={{ marginTop: 16 }}>
          <Space>
            <FileTextOutlined style={{ color: '#8c8c8c' }} />
            <Text type="secondary">{translateX('xml_not_available')}</Text>
          </Space>
        </div>
      )}
    </div>
  );
};

// *******************************************************************************
// FORM VIEW COMPONENT
// *******************************************************************************
const CancelFormView = ({ company, invoiceId, invoiceNumber, isNfce }) => {
  const isInvoiceIdProvided = !!invoiceId;
  const displayNumber = invoiceNumber || invoiceId;

  return (
    <div style={{ paddingTop: 8 }}>
      <Alert
        type="warning"
        showIcon
        icon={<CloseCircleOutlined />}
        message={isNfce ? translateX('cancel_nfce_info_title') : 'Cancelamento de NF-e'}
        description={
          isNfce
            ? translateX('cancel_nfce_info_description')
            : 'Esta operação cancela uma NF-e já autorizada. O cancelamento é permitido em até 24 horas após a autorização, desde que a mercadoria não tenha circulado. Caso o prazo tenha vencido, consulte seu contador sobre o Cancelamento Extemporâneo ou a emissão de uma NF-e de Estorno.'
        }
        style={{ marginBottom: 16 }}
      />

      <div style={{ marginBottom: 16 }}>
        <Text type="secondary">{translateX('company')}: </Text>
        <Text strong>{company?.name || company?.fullName || '-'}</Text>
      </div>

      {isInvoiceIdProvided && displayNumber && (
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">{translateX('invoice_number')}: </Text>
          <Text strong style={{ fontSize: 14 }}>
            {displayNumber}
          </Text>
        </div>
      )}

      <Form.Item
        name="invoiceId"
        label={translateX('invoice_id')}
        rules={[{ required: true, message: translateX('field_required') }]}
        initialValue={invoiceId}
        hidden
      >
        <Input />
      </Form.Item>



      <div style={{ marginTop: 16 }}>
        <SimpleTextArea
          name="justificativa"
          label="justificativa"
          placeholder={translateX('cancel_nfce_justification_placeholder')}
          minLength={15}
          maxLength={255}
          rows={3}
          showCount
          required
        />
        <Text type="secondary" style={{ fontSize: 11 }}>
          {translateX('justificativa_min_max')}
        </Text>
      </div>


    </div>
  );
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const CancelNfceModalStepContent = ({
  company,
  invoiceId,
  invoiceNumber,
  resultData,
  showResult,
  isNfce,
}) => {
  if (showResult && resultData) {
    if (resultData.success === false) {
      return <CancelErrorView resultData={resultData} />;
    }
    return <CancelResultView resultData={resultData} invoiceNumber={invoiceNumber} />;
  }
  return <CancelFormView company={company} invoiceId={invoiceId} invoiceNumber={invoiceNumber} isNfce={isNfce} />;
};

// *******************************************************************************
// STEP CONFIG
// *******************************************************************************
const cancelNfceModalStep = ({ company, invoiceId, invoiceNumber, resultData, showResult, isNfce }) => ({
  stepTitle: showResult ? 'result' : (isNfce ? 'cancel_nfce' : 'Cancelamento NF-e'),
  stepTooltip: showResult ? 'cancel_result_tooltip' : (isNfce ? 'cancel_nfce_tooltip' : 'Cancelamento de NF-e'),
  content: () => (
    <CancelNfceModalStepContent
      company={company}
      invoiceId={invoiceId}
      invoiceNumber={invoiceNumber}
      resultData={resultData}
      showResult={showResult}
      isNfce={isNfce}
    />
  ),
  validationFields: showResult ? [] : ['invoiceId', 'justificativa'],
  initialValues: invoiceId ? { invoiceId } : {},
  onNext: async ({ form }) => {
    if (showResult) {
      return true;
    }

    const values = form?.getFieldsValue(true) || {};
    const finalInvoiceId = invoiceId || values.invoiceId;
    const justificativa = values.justificativa;

    if (!finalInvoiceId) {
      form.setFields([
        {
          name: 'invoiceId',
          errors: [translateX('field_required')],
        },
      ]);
      return false;
    }

    if (!justificativa || justificativa.length < 15) {
      form.setFields([
        {
          name: 'justificativa',
          errors: [translateX('justificativa_min_15')],
        },
      ]);
      return false;
    }

    return true;
  },
});

export default cancelNfceModalStep;
